import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaDumbbell, FaClock, FaListOl, FaCheckCircle, FaExclamationTriangle, FaLightbulb, FaImage, FaCopy, FaEdit, FaTrash } from 'react-icons/fa';
import { cloneExercise, deleteUserExercise } from '../../services/fitnessService';
import { cloneCoachExercise } from '../../services/coachFitnessService';
import ConfirmModal from '../ConfirmModal';
import toast from 'react-hot-toast';
import SEO from '../SEO';
import { absoluteUrl, buildBreadcrumbs } from '../../utils/seoConfig';
import './ExercisePage.css';

const ExercisePage = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const isLoggedIn = !!localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isCoach = user?.isAffiliate && user?.fitnessEnabled;

  const handleClone = async () => {
    setCloning(true);
    try {
      if (isCoach) {
        const cloned = await cloneCoachExercise(exerciseId);
        toast.success('Exercise cloned to your coach library!');
        navigate(`/coach/exercise/${cloned._id}/edit`);
      } else {
        const cloned = await cloneExercise(exerciseId);
        toast.success('Exercise cloned! Redirecting to editor...');
        navigate(`/fitness/exercise/${cloned._id}/edit`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to clone exercise');
    } finally {
      setCloning(false);
    }
  };

  useEffect(() => {
    fetchExercise();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId]);

  const fetchExercise = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/fitness/exercises/${exerciseId}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      if (!response.ok) throw new Error('Failed to fetch exercise');
      const data = await response.json();
      setExercise(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs} seconds`;
    if (secs === 0) return `${mins} minute${mins > 1 ? 's' : ''}`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'strength': return '#8b5cf6';
      case 'cardio': return '#ef4444';
      case 'flexibility': return '#3b82f6';
      case 'mobility': return '#10b981';
      case 'core': return '#f59e0b';
      case 'warmup': return '#22c55e';
      case 'cooldown': return '#06b6d4';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="exercise-page">
        <div className="exercise-loading">
          <div className="loading-spinner" />
          <p>Loading exercise...</p>
        </div>
      </div>
    );
  }

  if (error || !exercise) {
    return (
      <div className="exercise-page">
        <div className="exercise-error">
          <p>Error loading exercise: {error || 'Exercise not found'}</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const canEditOwnExercise =
    isLoggedIn &&
    user?.sub &&
    exercise.userId &&
    exercise.userId === user.sub &&
    exercise._id;

  const canDeleteExercise = Boolean(
    isLoggedIn &&
      exercise._id &&
      (user?.isAdmin || (!isCoach && exercise.userId && exercise.userId === user.sub))
  );

  const handleDeleteExercise = async () => {
    if (!exercise._id) return;
    setDeleteLoading(true);
    try {
      await deleteUserExercise(exercise._id);
      toast.success('Exercise deleted');
      setDeleteModalOpen(false);
      navigate('/fitness/exercises');
    } catch (err) {
      toast.error(err.message || 'Failed to delete exercise');
    } finally {
      setDeleteLoading(false);
    }
  };

  const exerciseJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ExercisePlan',
    name: exercise.name,
    description: exercise.description,
    url: absoluteUrl(`/fitness/exercise/${exercise.exerciseId || exercise._id}`),
    exerciseType: exercise.category,
    activityType: exercise.category,
    workoutPlan: {
      '@type': 'HowTo',
      name: `How to do ${exercise.name}`,
      description: exercise.description,
      step: (exercise.instructions || []).map((ins, idx) => ({
        '@type': 'HowToStep',
        position: ins.step ?? idx + 1,
        text: ins.text || (typeof ins === 'string' ? ins : ''),
      })),
    },
    additionalProperty: [
      ...(exercise.targetMuscles || []).map((m) => ({
        '@type': 'PropertyValue',
        name: 'Target muscle',
        value: m,
      })),
      ...(exercise.equipmentNames || []).map((e) => ({
        '@type': 'PropertyValue',
        name: 'Equipment',
        value: e,
      })),
      ...(exercise.difficulty
        ? [{ '@type': 'PropertyValue', name: 'Difficulty', value: exercise.difficulty }]
        : []),
    ],
  };

  const exerciseBreadcrumbs = buildBreadcrumbs([
    { name: 'Home', url: '/' },
    { name: 'Exercises', url: '/fitness/exercises' },
    { name: exercise.name, url: `/fitness/exercise/${exercise.exerciseId || exercise._id}` },
  ]);

  return (
    <div className="exercise-page">
      <SEO
        title={`${exercise.name} — Exercise Guide, Form & Target Muscles`}
        description={
          exercise.description?.slice(0, 200) ||
          `Learn how to do ${exercise.name}. Step-by-step instructions, target muscles, equipment needed, and difficulty.`
        }
        canonical={`/fitness/exercise/${exercise.exerciseId || exercise._id}`}
        type="article"
        jsonLd={[exerciseJsonLd, exerciseBreadcrumbs]}
      />
      <div className="exercise-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
          <span>Back</span>
        </button>

        <div className="exercise-badges">
          <span 
            className="category-badge"
            style={{ backgroundColor: getCategoryColor(exercise.category) }}
          >
            {exercise.category}
          </span>
          <span 
            className="difficulty-badge"
            style={{ backgroundColor: getDifficultyColor(exercise.difficulty) }}
          >
            {exercise.difficulty}
          </span>
        </div>

        <h1>{exercise.name}</h1>
        <p className="exercise-description">{exercise.description}</p>
        {isLoggedIn && (
          <div className="exercise-header-actions">
            {canEditOwnExercise && (
              <button
                type="button"
                className="edit-exercise-btn"
                onClick={() =>
                  navigate(isCoach ? `/coach/exercise/${exercise._id}/edit` : `/fitness/exercise/${exercise._id}/edit`)
                }
              >
                <FaEdit /> Edit exercise
              </button>
            )}
            <button className="clone-exercise-btn" onClick={handleClone} disabled={cloning}>
              <FaCopy /> {cloning ? 'Cloning...' : 'Clone & Customize'}
            </button>
            {canDeleteExercise && (
              <button
                type="button"
                className="exercise-delete-btn"
                onClick={() => setDeleteModalOpen(true)}
              >
                <FaTrash /> Delete
              </button>
            )}
          </div>
        )}
      </div>

      <div className="exercise-container">
        {/* Media Section */}
        <section className="media-section">
          <div className="media-placeholder">
            {exercise.video ? (
              (() => {
                // Extract YouTube video ID
                const videoUrl = exercise.video;
                const youtubeMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
                const videoId = youtubeMatch ? youtubeMatch[1] : null;
                
                if (videoId) {
                  return (
                    <iframe
                      className="exercise-video"
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={exercise.name}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  );
                } else {
                  // Fallback to image if video ID can't be extracted
                  return exercise.image ? (
                    <img src={exercise.image} alt={exercise.name} className="exercise-image" />
                  ) : (
                    <div className="media-empty">
                      <FaImage className="media-icon" />
                      <p>Demonstration image coming soon</p>
                    </div>
                  );
                }
              })()
            ) : exercise.image ? (
              <img src={exercise.image} alt={exercise.name} className="exercise-image" />
            ) : (
              <div className="media-empty">
                <FaImage className="media-icon" />
                <p>Demonstration image coming soon</p>
              </div>
            )}
          </div>
        </section>

        {/* Quick Stats */}
        <section className="quick-stats">
          {exercise.defaultSets && (
            <div className="stat-box">
              <FaListOl className="stat-icon" />
              <div className="stat-content">
                <span className="stat-value">{exercise.defaultSets}</span>
                <span className="stat-label">Sets</span>
              </div>
            </div>
          )}
          {exercise.measurementType === 'reps' && exercise.defaultReps && (
            <div className="stat-box">
              <FaDumbbell className="stat-icon" />
              <div className="stat-content">
                <span className="stat-value">{exercise.defaultReps}</span>
                <span className="stat-label">Reps</span>
              </div>
            </div>
          )}
          {exercise.measurementType === 'time' && exercise.defaultDuration && (
            <div className="stat-box">
              <FaClock className="stat-icon" />
              <div className="stat-content">
                <span className="stat-value">{formatDuration(exercise.defaultDuration)}</span>
                <span className="stat-label">Duration</span>
              </div>
            </div>
          )}
          {exercise.restBetweenSets > 0 && (
            <div className="stat-box">
              <FaClock className="stat-icon rest" />
              <div className="stat-content">
                <span className="stat-value">{exercise.restBetweenSets}s</span>
                <span className="stat-label">Rest</span>
              </div>
            </div>
          )}
        </section>

        {/* Target Muscles */}
        <section className="muscles-section">
          <h2>Target Muscles</h2>
          <div className="muscles-grid">
            <div className="muscle-group primary">
              <h4>Primary</h4>
              <div className="muscle-tags">
                {exercise.targetMuscles?.map((muscle, idx) => (
                  <span key={idx} className="muscle-tag primary">{muscle}</span>
                ))}
              </div>
            </div>
            {exercise.secondaryMuscles && exercise.secondaryMuscles.length > 0 && (
              <div className="muscle-group secondary">
                <h4>Secondary</h4>
                <div className="muscle-tags">
                  {exercise.secondaryMuscles.map((muscle, idx) => (
                    <span key={idx} className="muscle-tag secondary">{muscle}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Instructions */}
        {exercise.instructions && exercise.instructions.length > 0 && (
          <section className="instructions-section">
            <h2>How to Perform</h2>
            <ol className="instructions-list">
              {exercise.instructions
                .sort((a, b) => a.step - b.step)
                .map((instruction, idx) => (
                  <li key={idx}>
                    <div className="step-number">{instruction.step}</div>
                    <p>{instruction.text}</p>
                  </li>
                ))}
            </ol>
          </section>
        )}

        {/* Equipment */}
        {exercise.equipmentNames && exercise.equipmentNames.length > 0 && (
          <section className="equipment-section">
            <h2>Equipment Needed</h2>
            <div className="equipment-list">
              {exercise.equipmentNames.map((item, idx) => (
                <div key={idx} className="equipment-item">
                  <FaDumbbell className="equipment-icon" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Tips */}
        {exercise.tips && exercise.tips.length > 0 && (
          <section className="tips-section">
            <h2>
              <FaLightbulb className="section-icon tip" />
              Tips
            </h2>
            <ul className="tips-list">
              {exercise.tips.map((tip, idx) => (
                <li key={idx}>
                  <FaCheckCircle className="tip-icon" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Safety Notes */}
        {exercise.safetyNotes && exercise.safetyNotes.length > 0 && (
          <section className="safety-section">
            <h2>
              <FaExclamationTriangle className="section-icon warning" />
              Safety Notes
            </h2>
            <ul className="safety-list">
              {exercise.safetyNotes.map((note, idx) => (
                <li key={idx}>
                  <FaExclamationTriangle className="safety-icon" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Modifications */}
        {exercise.modifications && (
          <section className="modifications-section">
            <h2>Modifications</h2>
            <div className="modifications-grid">
              {exercise.modifications.easier && (
                <div className="modification-card easier">
                  <h4>Make it Easier</h4>
                  <p>{exercise.modifications.easier}</p>
                </div>
              )}
              {exercise.modifications.harder && (
                <div className="modification-card harder">
                  <h4>Make it Harder</h4>
                  <p>{exercise.modifications.harder}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Tags */}
        {exercise.tags && exercise.tags.length > 0 && (
          <section className="tags-section">
            <div className="exercise-tags">
              {exercise.tags.map((tag, idx) => (
                <span key={idx} className="exercise-tag">{tag}</span>
              ))}
            </div>
          </section>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        variant="dark"
        title="Delete this exercise?"
        message="This permanently removes the exercise. Workouts that include it may show a missing exercise until you edit them."
        confirmLabel="Delete exercise"
        cancelLabel="Cancel"
        danger
        loading={deleteLoading}
        loadingLabel="Deleting…"
        onConfirm={handleDeleteExercise}
        onCancel={() => !deleteLoading && setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default ExercisePage;

